import React, { useEffect, useState } from "react";
import { X, ExternalLink, ChevronLeft, ChevronRight } from "lucide-react";
import { ChangelogEntry } from "../../api/changelogAPI";
import MarkdownBody from "./MarkdownBody";
import { Button } from "../ui";

/**
 * "What's New" changelog modal — Avoca-style two-pane layout: a dated
 * timeline rail on the left, the selected entry (tags + rich body +
 * optional media) on the right, with Previous/Next pagination and a
 * "Full changelog" link in the footer.
 *
 * Read-only; entries come from the central DB via the backend. Author in
 * the management app.
 */

interface Props {
  entries: ChangelogEntry[];
  total: number;
  isDarkMode?: boolean;
  onClose: () => void;
  onViewFull?: () => void;
}

function fmtDate(d: string): string {
  try {
    return new Date(d + "T00:00:00").toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return d;
  }
}

const WhatsNewModal: React.FC<Props> = ({
  entries,
  total,
  isDarkMode = false,
  onClose,
  onViewFull,
}) => {
  const [selected, setSelected] = useState(0);

  // Esc closes.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowDown") setSelected((s) => Math.min(entries.length - 1, s + 1));
      if (e.key === "ArrowUp") setSelected((s) => Math.max(0, s - 1));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, entries.length]);

  const entry = entries[selected];

  const panel = isDarkMode ? "bg-gray-900 text-gray-100" : "bg-white text-gray-900";
  const railBg = isDarkMode ? "bg-gray-950/40" : "bg-gray-50";
  const muted = isDarkMode ? "text-gray-400" : "text-gray-500";
  const border = isDarkMode ? "border-gray-700" : "border-gray-200";

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="What's New"
      onClick={onClose}
    >
      <div
        className={`w-full max-w-4xl max-h-[85vh] rounded-xl shadow-2xl overflow-hidden flex flex-col ${panel}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`flex items-start justify-between px-5 py-4 border-b ${border}`}>
          <div>
            <h2 className="text-lg font-semibold">What's New</h2>
            <p className={`text-xs ${muted}`}>Product updates from Club Central</p>
          </div>
          <Button
            type="button"
            variant="neutral"
            size="sm"
            iconOnly
            onClick={onClose}
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Body: timeline rail + detail */}
        <div className="flex min-h-0 flex-1">
          {/* Left rail */}
          <ul className={`w-56 shrink-0 overflow-y-auto border-r ${border} ${railBg} py-2`}>
            {entries.map((e, i) => {
              const active = i === selected;
              return (
                <li key={e.id}>
                  <button
                    aria-label={`Show ${e.title}`}
                    onClick={() => setSelected(i)}
                    className={`w-full text-left px-4 py-2.5 flex gap-2 ${
                      active
                        ? isDarkMode
                          ? "bg-gray-800"
                          : "bg-white shadow-sm"
                        : "hover:bg-black/5"
                    }`}
                  >
                    <span className="mt-1.5 flex flex-col items-center">
                      <span
                        className={`w-2 h-2 rounded-full ${
                          active ? "bg-sky-500" : isDarkMode ? "bg-gray-600" : "bg-gray-300"
                        }`}
                      />
                    </span>
                    <span className="min-w-0">
                      <span className={`block text-[11px] ${muted}`}>{fmtDate(e.published_at)}</span>
                      <span
                        className={`block text-sm leading-snug ${
                          active ? "font-semibold" : ""
                        }`}
                      >
                        {e.title}
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>

          {/* Detail pane */}
          <div className="flex-1 overflow-y-auto px-6 py-5">
            {entry ? (
              <>
                <p className={`text-xs ${muted}`}>{fmtDate(entry.published_at)}</p>
                <div className="flex flex-wrap items-center gap-2 mt-1">
                  <h3 className="text-xl font-semibold">{entry.title}</h3>
                  {(entry.tags || []).map((t) => (
                    <span
                      key={t}
                      className={`text-[11px] px-2 py-0.5 rounded-full ${
                        isDarkMode ? "bg-gray-800 text-gray-300" : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {t}
                    </span>
                  ))}
                </div>

                {entry.media_url && entry.media_type === "image" && (
                  <img
                    src={entry.media_url}
                    alt=""
                    className="mt-4 rounded-lg border w-full object-cover"
                  />
                )}
                {entry.media_url && entry.media_type === "video" && (
                  <video aria-label={entry.title || "Video"} src={entry.media_url} controls className="mt-4 rounded-lg border w-full" />
                )}

                <MarkdownBody
                  className={`changelog-body mt-4 text-sm ${
                    isDarkMode ? "text-gray-200" : "text-gray-700"
                  }`}
                >
                  {entry.body}
                </MarkdownBody>
              </>
            ) : (
              <p className={`text-sm ${muted}`}>No updates yet.</p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className={`flex items-center justify-between px-5 py-3 border-t ${border}`}>
          {onViewFull ? (
            <button
              onClick={onViewFull}
              className="text-sm text-sky-600 hover:underline inline-flex items-center gap-1"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              Full changelog
            </button>
          ) : (
            <span />
          )}
          <div className={`flex items-center gap-3 text-sm ${muted}`}>
            <button
              disabled={selected === 0}
              onClick={() => setSelected((s) => Math.max(0, s - 1))}
              className="inline-flex items-center gap-1 disabled:opacity-40 hover:text-current"
            >
              <ChevronLeft className="w-4 h-4" /> Previous
            </button>
            <span>
              {entries.length ? selected + 1 : 0} of {total || entries.length}
            </span>
            <button
              disabled={selected >= entries.length - 1}
              onClick={() => setSelected((s) => Math.min(entries.length - 1, s + 1))}
              className="inline-flex items-center gap-1 disabled:opacity-40 hover:text-current"
            >
              Next <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WhatsNewModal;
